import React, { useMemo } from 'react';

import { SearchSelect } from '@/design-system/search-select';

import { warehouseApi } from '../api/warehouse.api';
import { useActivateOnFocus } from '../hooks/useActivateOnFocus';
import { usePagedPicker } from '../hooks/usePagedPicker';
import { fmtQty } from '../format';
import type { OpnameBatch, WarehouseStock } from '../types';
import { type PickerA11yProps, usePickerA11y } from './pickerA11y';
import { batchLabel } from './pickingEdit';

const NO_IDS: ReadonlySet<string> = new Set();

/** "FORE-CUP-12OZ · 20261201-100726-aB3x · Return · Exp 01 Dec 2026 · 95 tersedia". */
export const stockBatchLabel = (s: WarehouseStock): string =>
  `${s.sku} · ${batchLabel(s.batchNumber, s.expiredDate, s.qtyAvailable, s.stockType)}`;

const stockKeyOf = (s: WarehouseStock) => s.id;

interface StockBatchSelectProps extends PickerA11yProps {
  hubID: number | null;
  /** The bin whose stock is offered; nothing is listed without one. */
  storageID: string;
  /** Chosen stock row id ('' = none). */
  value: string;
  /** What the chosen row is called while it is not in the loaded page. */
  selectedLabel?: string;
  onPick: (stock: WarehouseStock) => void;
  /** Stock row ids already used on another row (left out of the options). */
  excludeIds?: ReadonlySet<string>;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  testId?: string;
}

/**
 * Server-searched, paged picker over the stock rows of ONE bin — `sku · batch
 * · expiry · available` per option — for the screens that move or count stock
 * batch by batch (storage transfer). Asks nothing until the user reaches it,
 * debounces a typed search (SKU, item or batch number; the stale request is
 * aborted) and pages the list on scroll. Batches with nothing available are
 * not offered. The caller keeps the picked row itself.
 */
export const StockBatchSelect: React.FC<StockBatchSelectProps> = ({
  hubID,
  storageID,
  value,
  selectedLabel,
  onPick,
  excludeIds = NO_IDS,
  disabled,
  invalid,
  placeholder = 'Pilih produk & batch',
  testId,
  'aria-label': ariaLabel,
}) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  const { active, onFocus } = useActivateOnFocus();
  const picker = usePagedPicker<WarehouseStock>({
    scope: `${hubID ?? ''}|${storageID}`,
    enabled: active && !disabled && !!hubID && !!storageID,
    keyOf: stockKeyOf,
    fetchPage: ({ search, page, limit }, signal) =>
      warehouseApi.listStocks(
        { hubID: hubID as number, storageID, search, page, limit },
        { signal }
      ),
  });
  const options = useMemo(
    () =>
      picker.rows
        .filter(
          (s) => s.id === value || (s.qtyAvailable > 0 && !excludeIds.has(s.id))
        )
        .map((s) => ({ value: s.id, label: stockBatchLabel(s) })),
    [picker.rows, value, excludeIds]
  );

  return (
    <div ref={ref} className="min-w-0" data-testid={testId} onFocus={onFocus}>
      <SearchSelect
        options={options}
        value={value}
        selectedLabelFallback={selectedLabel}
        onChange={(id) => {
          const row = picker.rows.find((s) => s.id === id);
          if (!row) return;
          onPick(row);
          picker.setSearch('');
        }}
        onSearchChange={picker.setSearch}
        disableLocalFilter
        hasMore={picker.hasMore}
        onLoadMore={picker.loadMore}
        loading={picker.loading}
        placeholder={placeholder}
        allowClear={false}
        disabled={disabled}
        error={invalid}
        emptyMessage={
          picker.error
            ? `Gagal memuat stok: ${picker.error.message}`
            : !storageID
              ? 'Pilih storage asal dulu'
              : picker.searching
                ? 'Produk atau batch tidak ditemukan'
                : 'Tidak ada stok tersedia di storage ini'
        }
      />
    </div>
  );
};

/* ------------------------------ stock opname ------------------------------ */

/** "Slot A1 · FORE-CUP-12OZ · 2026… · Exp 01 Dec 2026 · 100 on hand". */
export const opnameBatchLabel = (b: OpnameBatch): string =>
  [
    b.storageName,
    b.sku,
    batchLabel(b.batchNumber, b.expiredDate, undefined, b.stockType),
    `${fmtQty(b.qtyOnHand)} on hand`,
    ...(b.locked ? [`Sedang di-opname di ${b.lockedBy ?? 'opname lain'}`] : []),
  ].join(' · ');

interface OpnameBatchSelectProps extends PickerA11yProps {
  hubID: number | null;
  /** The opname's client; nothing is listed without one. */
  clientID: number | null;
  /** Narrow the lots to one storage ('' = every storage of the hub). */
  storageID?: string;
  /** Lot key (`storage|sku|batch|type`) of the row's chosen lot ('' = none). */
  value: string;
  /** What the chosen lot is called while it is not in the loaded page. */
  selectedLabel?: string;
  onPick: (batch: OpnameBatch) => void;
  /** A lot held by another open opname was clicked: it can not be counted. */
  onBlocked?: (batch: OpnameBatch) => void;
  /** Lot keys already used on another row (left out of the options). */
  excludeKeys?: ReadonlySet<string>;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  testId?: string;
}

/** A lot is one stock row: the same batch as REGULAR and RETURN is two lots. */
const opnameKeyOf = (b: OpnameBatch) =>
  `${b.storageID}|${b.sku}|${b.batchNumber}|${b.stockType ?? ''}`;

/**
 * The stock opname's counterpart of {@link StockBatchSelect}: the same
 * server-searched, paged picker (SKU, item or batch search, optional storage
 * narrowing), but over `available-batches` — every lot of the client in the
 * hub with what is on hand, on any bin — so a lot with nothing available is
 * still countable. A lot another open opname is counting is listed with the
 * code of that opname and can not be picked (the kit options have no disabled
 * state, so a click is answered through `onBlocked`).
 */
export const OpnameBatchSelect: React.FC<OpnameBatchSelectProps> = ({
  hubID,
  clientID,
  storageID = '',
  value,
  selectedLabel,
  onPick,
  onBlocked,
  excludeKeys = NO_IDS,
  disabled,
  invalid,
  placeholder = 'Cari SKU atau batch',
  testId,
  'aria-label': ariaLabel,
}) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  const { active, onFocus } = useActivateOnFocus();
  const picker = usePagedPicker<OpnameBatch>({
    scope: `${hubID ?? ''}|${clientID ?? ''}|${storageID}`,
    enabled: active && !disabled && !!hubID && !!clientID,
    keyOf: opnameKeyOf,
    fetchPage: ({ search, page, limit }, signal) =>
      warehouseApi.listOpnameBatches(
        {
          hubID: hubID as number,
          clientID: clientID as number,
          storageID: storageID || null,
          search,
          page,
          limit,
        },
        { signal }
      ),
  });
  const options = useMemo(
    () =>
      picker.rows
        .filter((b) => {
          const key = opnameKeyOf(b);
          return key === value || !excludeKeys.has(key);
        })
        .map((b) => ({ value: opnameKeyOf(b), label: opnameBatchLabel(b) })),
    [picker.rows, value, excludeKeys]
  );

  return (
    <div ref={ref} className="min-w-0" data-testid={testId} onFocus={onFocus}>
      <SearchSelect
        options={options}
        value={value}
        selectedLabelFallback={selectedLabel}
        onChange={(key) => {
          const row = picker.rows.find((b) => opnameKeyOf(b) === key);
          if (!row) return;
          picker.setSearch('');
          if (row.locked) onBlocked?.(row);
          else onPick(row);
        }}
        onSearchChange={picker.setSearch}
        disableLocalFilter
        hasMore={picker.hasMore}
        onLoadMore={picker.loadMore}
        loading={picker.loading}
        placeholder={placeholder}
        allowClear={false}
        disabled={disabled}
        error={invalid}
        emptyMessage={
          picker.error
            ? `Gagal memuat stok: ${picker.error.message}`
            : !clientID
              ? 'Pilih client dulu'
              : picker.rows.length > 0
                ? 'Semua batch yang cocok sudah ada di daftar'
                : picker.searching
                  ? 'Produk atau batch tidak ditemukan'
                  : 'Belum ada stok untuk client ini'
        }
      />
    </div>
  );
};
